import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { FaAd, FaExclamationCircle, FaMinus, FaPlus, FaTimes } from "react-icons/fa";
import FullPageSpinner from "../../components/FullPageSpinner";
import { GuestForm } from "../../Modal/bookings/OnlineBookingModal";
import { attachCurrentTime, dateDuration, getBookingBusinessDate } from "../../utils/date";
import { useToast } from "../../context/ToastContext";
import { useModal } from "../../context/ConfirmModalContext";
import { sendRequest } from "../../utils/api";
import Room from "../../models/Room";
import BookedRoom from "../../models/BookedRoom";
import { DEFAULT_CUSTOMER_EMAIL } from "../../constants";
import { getCurrentHostInfo } from "../../utils/helper-function";



/* ================= TYPES ================= */
type SelectedRooms = {
    [roomId: string]: number;
};


/* ================= ROOM CARD ================= */
function RoomCard({
    room,
    quantity,
    onIncrease,
    onDecrease,
}: {
    room: Room & { available: number };
    quantity: number;
    onIncrease: () => void;
    onDecrease: () => void;
}) {
    return (
        <div className="p-5 rounded-2xl border bg-white shadow-sm hover:shadow-md transition-all duration-200">

            {/* Header */}
            <div className="flex justify-between items-start">
                <div>
                    <h2 className="font-semibold text-lg text-gray-800 capitalize">
                        {room.name}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        {room.available} room{room.available !== 1 ? "s" : ""} available
                    </p>
                </div>

                <div className="text-right">
                    <p className="text-lg font-bold text-blue-600">
                        ₦{room.cashPrice.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400">per night</p>
                </div>
            </div>

            {/* Divider */}
            <div className="border-t my-4"></div>

            {/* Quantity Selector */}
            <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">Select rooms</p>

                <div className="flex items-center space-x-3">
                    <button
                        onClick={onDecrease}
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition disabled:opacity-40"
                        disabled={quantity === 0}
                    >
                        {FaMinus({ size: 12 })}
                    </button>

                    <span className="font-semibold text-lg w-6 text-center">
                        {quantity}
                    </span>

                    <button
                        onClick={onIncrease}
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-40"
                        disabled={quantity >= room.available}
                    >
                        {FaPlus({ size: 12 })}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ================= MAIN PAGE ================= */
export default function CheckAvailability() {
    const { showToast } = useToast();
    const { openModal, closeModal } = useModal();

    const [checkIn, setCheckIn] = useState("");
    const [checkOut, setCheckOut] = useState("");
    const [availableRooms, setAvailableRooms] = useState<(Room & { available: number })[]>([]);
    const [selectedRooms, setSelectedRooms] = useState<SelectedRooms>({});
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    const [loading, setLoading] = useState(false);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [address, setAddress] = useState("");
    const [state, setState] = useState("");
    const [gender, setGender] = useState("");

    const [showGuestModal, setShowGuestModal] = useState(false);
    const [showAnalysisModal, setShowAnalysisModal] = useState(false);

    const nights = dateDuration(checkIn, checkOut);

    // const [hotelId, setHotelId] = useState("");


    /* ================= CALCULATIONS ================= */
    const totalRooms = Object.values(selectedRooms).reduce(
        (a, b) => a + b,
        0
    );

    const totalAmount = availableRooms.reduce((sum, room) => {
        const qty = selectedRooms[room.id] || 0;
        return sum + qty * room.cashPrice * nights;
    }, 0);

    const selectedRoomDetails = availableRooms.filter(
        (room) => selectedRooms[room.id]
    );

    const initiatePayment = async () => {
        setLoading(true);
        // const qty = selectedRooms[room.id];

        try {
            const billId = uuidv4();

            const callback_url = (
                `${window.location.origin}/payment/verify?billId=${billId}&type=onlineBooking`
            );

            const url = "/payment/initiate";
            const response = await sendRequest("POST", url, {
                amount: totalAmount, callback_url,
                email: email || DEFAULT_CUSTOMER_EMAIL,
            });

            const customerId = uuidv4();

            const businessDate = getBookingBusinessDate();

            const createdAt = businessDate;

            const guestData = {
                createdAt,
                id: customerId,
                name,
                phone,
                email,
                address,
                gender: gender,
                state,
            };

            const bookedRoomData = {
                createdAt,
                guestName: name,
                duration: nights,
                bookingType: "reservation",
                checkInDate: attachCurrentTime(checkIn),
                checkOutDate: checkOut,
                paymentMethod: "online",
                isPaidByGuest: true,
                isUse: true,
                isPaid: true,
                discount: 0,
            };

            const roomsData = selectedRoomDetails.map((room) => ({
                ...room,
                quantity: selectedRooms[room.id],
            }));

            const data = {
                roomsData,
                bookedRoomData, guestData,
                billData: { id: billId, }
            };


            localStorage.setItem(
                "data", JSON.stringify(data)
            );
            window.location.href = response.data.data.checkout_url;
        } catch (err: any) {
            console.log(err);
            setLoading(false);
            if (err?.response?.status === 409) {
                return openModal(
                    "Conflict⚠️",
                    err.response.data.error,
                    () => { },
                    false,
                    "Close"
                );
            }
            showToast(
                "Room Booking failed. " +
                "Please check your network and try again.",
                { type: "error" }
            );
        }
    }


    const checkAvailability = async () => {
        setLoading(true);
        setAvailableRooms([]);

        // 🔹 Detect subdomain for multi-tenant context
        const hostname = window.location.hostname; // e.g. demo-hotel.wiventory.com
        const subdomain = hostname.split('.')[0];  // e.g. demo-hotel
        // console.log("Current tenant:", subdomain);
        try {
            const url = (
                `/rooms/available?` +
                `checkIn=${checkIn}&` +
                `checkOut=${checkOut}&` +
                `subdomain=${subdomain}`
            );
            const response = await sendRequest("GET", url);
            const { rooms, hotelId } = response;
            setAvailableRooms(rooms);
            // setHotelId(hotelId);
            setSelectedRooms({});

        } catch (err: any) {
            console.log(err)
            // const error = errorMessage(err.status);
            // if (error) {
            //     return openModal(error.title, error.msg, closeModal, false, "Close");
            // } else {
            //     console.error("Background fetch error:", err);
            // }
        } finally {
            setLoading(false);
        }
    };

    /* ================= HANDLERS ================= */
    const increaseRoom = (roomId: string) => {
        setSelectedRooms((prev) => ({
            ...prev,
            [roomId]: (prev[roomId] || 0) + 1,
        }));
    };

    const decreaseRoom = (roomId: string) => {
        setSelectedRooms((prev) => {
            const current = prev[roomId] || 0;
            if (current <= 1) {
                const updated = { ...prev };
                delete updated[roomId];
                return updated;
            }
            return {
                ...prev,
                [roomId]: current - 1,
            };
        });
    };


    return (
        <div className="min-h-screen bg-gray-50 p-6 pb-24">
            <div className=" mx-auto">

                {showGuestModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="bg-white rounded-2xl shadow-lg w-full max-w-3xl p-6 relative">
                            {/* Close button */}
                            <button
                                onClick={() => setShowGuestModal(false)}
                                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                            >
                                {FaTimes({})}
                            </button>

                            {/* Guest Form */}
                            <GuestForm
                                name={name}
                                email={email}
                                phone={phone}
                                address={address}
                                state={state}
                                gender={gender}
                                setName={setName}
                                setEmail={setEmail}
                                setPhone={setPhone}
                                setAddress={setAddress}
                                setGender={setGender}
                            />

                            {/* Action buttons */}
                            <div className="mt-6 flex justify-between">
                                <button
                                    onClick={() => setShowGuestModal(false)}
                                    className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                                >
                                    Cancel
                                </button>

                                <button
                                    onClick={() => {
                                        // handle submit or continue
                                        if (!!(name && phone && address)) {
                                            const missingFields: string[] = [];
                                            //if (!guestName) missingFields.push("Guest Name");
                                            if (!name) missingFields.push("Name");
                                            if (!phone) missingFields.push("Phone Number");
                                            if (!address) missingFields.push("Address");
                                            if (!gender) missingFields.push("Gender");

                                            if (missingFields.length > 0) {
                                                return openModal(
                                                    "Missing Fields",
                                                    `Please fill in: ${missingFields.join(", ")}`,
                                                    closeModal,
                                                    false,
                                                    "Close"
                                                );
                                            }


                                            setShowSummaryModal(true);
                                            setShowGuestModal(false);
                                        }
                                    }}
                                    className={`px-6 py-2 text-white rounded-lg 
                                    ${!(name && phone && address) ? "bg-blue-600 opacity-40 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`
                                    }
                                >
                                    Continue
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showSummaryModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
                        <div className="bg-white w-full max-w-lg rounded-2xl p-6 shadow-xl relative">

                            {/* CLOSE BUTTON */}
                            <button
                                onClick={() => setShowSummaryModal(false)}
                                className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"
                            >
                                {FaTimes({ size: 18 })}
                            </button>

                            {/* HEADER */}
                            <h2 className="text-lg font-semibold mb-4 text-gray-800">
                                Booking Summary 🧾
                            </h2>

                            {/* ROOM LIST */}
                            <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                                {selectedRoomDetails.map((room) => {
                                    const qty = selectedRooms[room.id];
                                    const subtotal = qty * room.cashPrice * nights;

                                    return (
                                        <div
                                            key={room.id}
                                            className="flex justify-between items-center border-b pb-2"
                                        >
                                            <div>
                                                <p className="font-medium text-gray-800">{room.name}</p>
                                                <p className="text-xs text-gray-500">
                                                    {qty}  × ₦{room?.cashPrice.toLocaleString()} × {nights} night{nights !== 1 ? "s" : ""}
                                                </p>
                                            </div>

                                            <p className="font-semibold text-gray-800">
                                                ₦{subtotal.toLocaleString()}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* TOTAL */}
                            <div className="mt-4 border-t pt-4 space-y-1">
                                <div className="flex justify-between text-sm text-gray-500">
                                    <span>Total Rooms</span>
                                    <span>{totalRooms}</span>
                                </div>

                                <div className="flex justify-between font-semibold text-base text-gray-800">
                                    <span>Grand Total</span>
                                    <span>₦{totalAmount.toLocaleString()}</span>
                                </div>
                            </div>

                            {/* ACTIONS */}
                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => {
                                        setShowSummaryModal(false)
                                    }}
                                    className="flex-1 border border-gray-300 text-gray-600 rounded-lg py-2 text-sm hover:bg-gray-100"
                                >
                                    Cancel
                                </button>


                                <button
                                    onClick={() => {
                                        initiatePayment();
                                    }}
                                    disabled={loading}
                                    className={`flex-1 rounded-lg py-2 text-sm font-medium flex items-center justify-center gap-2 transition-all duration-200
    ${loading
                                            ? "bg-gray-400 text-white cursor-not-allowed"
                                            : "bg-blue-600 hover:bg-blue-700 text-white"
                                        }`}
                                >
                                    <span className="flex items-center gap-2">
                                        Pay Now
                                        {loading && (
                                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                        )}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {/* HEADER */}
                <div className="mb-6">
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                        Find Your Perfect Stay
                    </h1>
                    <p className="text-gray-500 italic mt-2 text-sm md:text-base">
                        Choose your dates and explore available rooms tailored for you
                    </p>
                </div>

                {/* <FullPageSpinner show={loading} /> */}

                {/* DATE INPUT */}
                <div className="bg-white p-5 rounded-2xl shadow-sm mb-6">
                    <div className="grid md:grid-cols-3 gap-4">
                        <input
                            type="date"
                            value={checkIn}
                            onChange={(e) => {
                                const start = e.target.value;

                                if (checkOut && start > checkOut) {
                                    showToast("Start date cannot be later than end date.", { type: "warning" });
                                    return;
                                }
                                setCheckIn(e.target.value);
                                setAvailableRooms([]);
                                setSelectedRooms({})
                            }}
                            className="border p-3 rounded-lg w-full"
                        />

                        <input
                            type="date"
                            value={checkOut}
                            onChange={(e) => {
                                const end = e.target.value;

                                if (!checkIn) {
                                    showToast("Please select a start date first.", { type: "info" });
                                    return;
                                }

                                if (end < checkIn) {
                                    showToast("End date cannot be earlier than start date.", { type: "warning" });
                                    return;
                                }
                                setCheckOut(e.target.value);
                                setAvailableRooms([]);
                                setSelectedRooms({});
                            }}
                            className="border p-3 rounded-lg w-full"
                        />
                        <button
                            onClick={checkAvailability}
                            disabled={loading || !checkIn || !checkOut}
                            className={`px-4 py-3 rounded-lg flex items-center justify-center gap-2 text-white
    ${loading || !checkIn || !checkOut
                                    ? "bg-blue-600 opacity-40 cursor-not-allowed"
                                    : "bg-blue-600 hover:bg-blue-700"
                                }`}
                        >
                            {loading && (
                                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            )}

                            {loading ? "Checking..." : "Check Availability"}
                        </button>
                    </div>
                </div>

                {/* ROOMS */}
                {availableRooms.length > 0 && (
                    <>
                        <h2 className="text-xl font-semibold mb-4">
                            Available Rooms
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {availableRooms.map((room) => (
                                <RoomCard
                                    key={room.id}
                                    room={room}
                                    quantity={selectedRooms[room.id] || 0}
                                    onIncrease={() => increaseRoom(room.id)}
                                    onDecrease={() => decreaseRoom(room.id)}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* STICKY SUMMARY */}
            {totalRooms > 0 && (
                <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg p-4">
                    <div className="max-w-5xl mx-auto flex justify-between items-center">

                        {/* Left side: Room summary and info icon */}
                        <div className="flex flex-col md:flex-row md:items-center md:space-x-3">
                            <div className="flex items-center space-x-2">
                                <p className="font-medium">
                                    {totalRooms} room{totalRooms !== 1 ? "s" : ""} selected
                                </p>
                                {/* Info Icon */}
                                <button
                                    onClick={() => setShowAnalysisModal(true)}
                                    className="text-gray-500 hover:text-gray-700"
                                    title="See price breakdown"
                                >
                                    {FaExclamationCircle({ size: 20 })}
                                </button>
                            </div>
                            <p className="text-sm text-gray-600 mt-1 md:mt-0">
                                Total: ₦{totalAmount.toLocaleString()}
                            </p>
                        </div>

                        {/* Right side: Proceed button */}
                        <button
                            onClick={() => setShowGuestModal(true)}
                            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg mt-2 md:mt-0"
                        >
                            Proceed →
                        </button>

                    </div>
                </div>
            )}

            {showAnalysisModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-6 relative">
                        <button
                            onClick={() => setShowAnalysisModal(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                        >
                            {FaTimes({})}
                        </button>

                        <h3 className="text-lg font-semibold mb-4">Price Breakdown 🧾</h3>
                        <ul className="space-y-2 text-gray-700">
                            {availableRooms.map((room) => {
                                const qty = selectedRooms[room.id] || 0;
                                if (qty === 0) return null;
                                return (
                                    <li key={room.id}>
                                        {room.name}: ₦{room?.cashPrice?.toLocaleString()} × {qty} room{qty > 1 ? "s" : ""} × {nights} night{nights !== 1 ? "s" : ""} = ₦{(room.cashPrice * qty * nights).toLocaleString()}
                                    </li>
                                );
                            })}
                        </ul>
                        <p className="mt-4 font-medium">
                            Total: ₦{totalAmount.toLocaleString()}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}